import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {
  Box,
  Button,
  CircularProgress,
  Input,
  Typography,
} from '@mui/material';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { CaretDown, MagnifyingGlass } from 'phosphor-react';
import { UserService } from '../../Service/UserService';
import { useDebounceEffect } from '../../Utilis/useDebounceEffect';
import { useAppDispatch } from '../../store/hooks';
import { createNotification } from '../../store/notifications/notification-slice';
import { User } from '../../Utilis/Types/User';
import * as _ from 'lodash';
import ShareIcon from '../../assets/Icons/share-icon';
import { AuthService } from '../../Service/AuthService';
import { LocalStorageService } from '../../Service/LocalStorageService';
import { setClient, setToken } from '../../store/user/user-slice';

interface Column {
  id: 'name' | 'email' | 'status' | 'type';
  label: string;
  minWidth?: number;
  align?: 'left';
  format?: (value: number) => string;
}

const columns: readonly Column[] = [
  { id: 'name', label: 'Contact Name', minWidth: 130 },
  { id: 'email', label: 'User Email', minWidth: 130 },
  {
    id: 'status',
    label: 'Status',
    minWidth: 160,
    align: 'left',
  },
  {
    id: 'type',
    label: 'Type',
    minWidth: 100,
    align: 'left',
  },
  // {
  //   id: "type", // New column ID
  //   label: "Buttton", // Empty label to hide it
  //   minWidth: 50, // Adjust the width as needed
  //   align: "left", // Adjust the alignment as needed
  // },
];

interface Data {
  name: string;
  email: string;
  status: string;
  type: string;
  id: string;
}

const modifyUser = (user: User) => ({
  name: user?.name + ' ' + user?.lastName,
  email: user?.email,
  status: user?.status,
  type: user?.type,
  id: user?.id,
});

function AdminDashboard() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sort, setSort] = useState('A to Z');
  const [status, setStatus] = useState('All');
  const [type, setType] = useState('All');
  const [rows, setRows] = useState<Data[]>([]);
  const [meta, setMeta] = useState(0);
  const [setOpenFilter, openFilter] = useOutletContext<any>();
  const [searchQuery, setSearchQuery] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();
  const handleChangePage = async (event: unknown, newPage: number) => {
    try {
      setPage(newPage);
      const response = await UserService.getAllUsers(
        newPage + 1,
        rowsPerPage,
        type,
        status
      );

      const uniqBy = _.uniqBy(
        [...rows, ...response?.data?.map(modifyUser)],
        'id'
      );

      setRows(uniqBy);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong! Please try again later.',
        })
      );
    }
  };

  const navigateToClient = async (userId: string) => {
    if (!userId) {
      return;
    }

    const response = await AuthService.accessClientView({ clientId: userId });
    LocalStorageService.setItem('accessToken', response.data.accessToken);

    appDispatch(setToken(response.data.accessToken));
    appDispatch(setClient(response.data));

    navigate('/home');
  };

  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      setRowsPerPage(+event.target.value);
      setPage(0);

      const response = await UserService.getAllUsers(
        page + 1,
        +event.target.value,
        type,
        status
      );
      setRows(response?.data?.map(modifyUser));
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong! Please try again later.',
        })
      );
    }
  };

  const onSearch = async (searchQuery: string) => {
    setIsLoading(true);
    try {
      const name = searchQuery?.split(' ')?.[0];
      const lastName = searchQuery?.split(' ')?.[1];
      const response = await UserService.searchUser(name, lastName);

      setRows(
        response?.data?.map(user => ({
          name: user?.name + ' ' + user?.lastName,
          email: user?.email,
          status: user?.status,
          type: user?.type,
          id: user?.id,
        }))
      );
      setMeta(response?.meta?.total);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong! Please try again later.',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useDebounceEffect(
    () => {
      if (searchQuery !== null) {
        onSearch(searchQuery);
      }
    },
    [searchQuery],
    1000
  );

  const statusAndTypes = {
    status: [
      'All',
      'Active Client',
      'Admin/Car. Mgr.',
      'Agency',
      'Bus. Manager',
      'Business',
      'Business Relationship',
      'Business- Do not contact',
      'Child of Client',
      "Client's Employer",
      "Client's Professional Contact",
      'Closed',
      'Contact Manager',
      'Deceased',
      'Do not contact',
      'Employee',
      'Enrolled',
      'Family',
      'Friend',
      'Imported',
      'Inactive Client',
      'Independant Producer',
      'Lead',
      'Not Specified',
      'Pending',
      'Producer',
      'Professional',
      'Professional Referral',
      'Prospect',
      'Quote Manager',
      'Quoting',
      'Related',
      'Self Generated',
      'Spouse of Client',
      'Suspect',
      'VA',
      'Vendor',
    ],
    type: ['All', 'Individual', 'Business', 'Association', 'Trust', 'Union'],
  };

  const onChangingStatus = async (status: string) => {
    setIsLoading(true);
    try {
      const response = await UserService.getAllUsers(
        page + 1,
        rowsPerPage,
        type,
        status
      );
      setRows(response?.data?.map(modifyUser));
      setMeta(response?.meta?.total);
      setStatus(status);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong! Please try again later.',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const onChangingType = async (type: string) => {
    setIsLoading(true);
    try {
      const response = await UserService.getAllUsers(
        page + 1,
        rowsPerPage,
        type,
        status
      );
      setType(type);
      setRows(response?.data?.map(modifyUser));
      setMeta(response?.meta?.total);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong! Please try again later.',
        })
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await UserService.getAllUsers(
          page + 1,
          rowsPerPage,
          type,
          status
        );
        setRows(response?.data?.map(modifyUser));

        setMeta(response?.meta?.total);
      } catch (error) {
        appDispatch(
          createNotification({
            type: 'error',
            message: 'Something went wrong! Please try again later.',
          })
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const sortedRows = rows.sort((a, b) => {
    if (sort === 'A to Z') {
      return a.name.localeCompare(b.name);
    } else {
      return b.name.localeCompare(a.name);
    }
  });

  return (
    <Box m={'32px 48px 0 48px'}>
      <Box
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Box display={'flex'} gap={'100px'}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#736977',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              position: 'relative',
            }}
            variant='h1'
            onClick={() =>
              setOpenFilter?.({
                status: false,
                type: false,
                sort: !openFilter.sort,
              })
            }
          >
            Sort: {sort} <CaretDown size={18} weight='fill' color={'#736977'} />
            {openFilter.sort && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translate(-50%, 0)',
                  zIndex: 3,
                }}
              >
                <Box
                  bgcolor={'#FFFFFF'}
                  border={'1px solid #E3DDE6'}
                  borderRadius={'4px'}
                  padding={'5px 5px'}
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                    zIndex: 3,
                  }}
                >
                  <Box p={'5px'} width={'70px'}>
                    <Box
                      display={'flex'}
                      alignItems={'left'}
                      flexDirection={'column'}
                      gap={'5px'}
                    >
                      {['A to Z', 'Z to A'].map(item => (
                        <Typography
                          variant='body1'
                          key={item}
                          color={'#202A2D'}
                          fontWeight={500}
                          fontSize={'14px'}
                          sx={{
                            cursor: 'pointer',
                            color: sort === item ? '#0083E4' : '#202A2D',
                            '&:hover': {
                              color: '#0083E4',
                            },
                          }}
                          onClick={() => setSort(item)}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Typography>

          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#736977',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              position: 'relative',
              textTransform: 'capitalize',
            }}
            variant='h1'
            onClick={() =>
              setOpenFilter?.({
                sort: false,
                type: false,
                status: !openFilter.status,
              })
            }
          >
            Status: {status}{' '}
            <CaretDown size={18} weight='fill' color={'#736977'} />
            {openFilter.status && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translate(-50%, 0)',
                  zIndex: 3,
                }}
              >
                <Box
                  bgcolor={'#FFFFFF'}
                  border={'1px solid #E3DDE6'}
                  borderRadius={'4px'}
                  padding={'5px 5px'}
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                    zIndex: 3,
                    width: '250px',
                  }}
                >
                  <Box
                    p={'5px'}
                    width={'250px'}
                    height={'200px'}
                    overflow={' hidden auto'}
                  >
                    <Box
                      display={'flex'}
                      alignItems={'left'}
                      flexDirection={'column'}
                      gap={'5px'}
                    >
                      {statusAndTypes.status.map(item => (
                        <Typography
                          variant='body1'
                          color={'#202A2D'}
                          key={item}
                          fontWeight={500}
                          fontSize={'14px'}
                          sx={{
                            cursor: 'pointer',
                            textTransform: 'capitalize',
                            color: status === item ? '#0083E4' : '#202A2D',
                            '&:hover': {
                              color: '#0083E4',
                            },
                          }}
                          onClick={() => onChangingStatus(item)}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Typography>

          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#736977',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              position: 'relative',
            }}
            variant='h1'
            onClick={() =>
              setOpenFilter?.({
                sort: false,
                status: false,
                type: !openFilter.type,
              })
            }
          >
            Type: {type} <CaretDown size={18} weight='fill' color={'#736977'} />
            {openFilter.type && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translate(-50%, 0)',
                  zIndex: 3,
                }}
              >
                <Box
                  bgcolor={'#FFFFFF'}
                  border={'1px solid #E3DDE6'}
                  borderRadius={'4px'}
                  padding={'5px 5px'}
                  sx={{
                    position: 'absolute',
                    top: '10px',
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                    zIndex: 3,
                  }}
                >
                  <Box p={'5px'}>
                    <Box
                      display={'flex'}
                      alignItems={'left'}
                      flexDirection={'column'}
                      gap={'5px'}
                    >
                      {statusAndTypes.type?.map(item => (
                        <Typography
                          variant='body1'
                          color={'#202A2D'}
                          key={item}
                          fontWeight={500}
                          fontSize={'14px'}
                          sx={{
                            cursor: 'pointer',
                            color: type === item ? '#0083E4' : '#202A2D',
                            '&:hover': {
                              color: '#0083E4',
                            },
                          }}
                          onClick={() => onChangingType(item)}
                        >
                          {item}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Typography>
        </Box>
        <Input
          placeholder='Search'
          disableUnderline={true}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '16px',
            borderBottom: '1px solid #DBE0E2',
            backgroundColor: 'transparent',
          }}
          onChange={e => setSearchQuery(e.target.value)}
          startAdornment={<MagnifyingGlass size={14} />}
        />
      </Box>

      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
          borderRadius: '8px',
          boxShadow:
            '0px 0.5px 3px rgba(14, 14, 14, 0.07), 0px 2px 6px 2px rgba(64, 64, 64, 0.12)',
        }}
      >
        <TableContainer sx={{ maxHeight: 650 }}>
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow>
                {columns.map(column => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      fontWeight: 600,
                      fontSize: '12px',
                      color: '#151219',
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '50vh',
                  width: '400% !important',
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <TableBody
                sx={{
                  '	.MuiTableRow-hover': {
                    '&:hover': {
                      backgroundColor: '#E5FAFF',
                    },
                  },
                }}
              >
                <>
                  {sortedRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(row => {
                      return (
                        <TableRow hover tabIndex={-1} key={row.name}>
                          {columns.map(column => {
                            const value = row[column.id];

                            return (
                              <TableCell
                                sx={
                                  column.id === 'name'
                                    ? {
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        color: '#0083E4',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: '20px 16px',
                                        textTransform: 'capitalize',
                                        borderBottom: 'none',
                                      }
                                    : {
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        borderBottom: 'none',
                                        textTransform:
                                          column.id === 'email'
                                            ? ''
                                            : 'capitalize',
                                        color: '#151219',
                                      }
                                }
                                key={column.id}
                                align={column.align}
                                onClick={() =>
                                  navigate(`/admin/user/${row.id}`)
                                }
                              >
                                {column.format && typeof value === 'number'
                                  ? column.format(value)
                                  : value}
                              </TableCell>
                            );
                          })}

                          <TableCell style={{ border: 'none' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                width: '163px',
                                height: '39px',
                                padding: '12px 42px 12px 24px',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Button
                                style={{
                                  color: 'var(--Primary-Color, #3293DB)',
                                  fontFamily: 'Inter',
                                  fontSize: '12px',
                                  fontStyle: 'normal',
                                  fontWeight: 500,
                                  lineHeight: '20px',
                                  textTransform: 'capitalize',
                                }}
                                onClick={() => navigateToClient(row.id)}
                              >
                                Client View
                              </Button>
                              <ShareIcon />
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </>
              </TableBody>
            )}
          </Table>
        </TableContainer>
        {!isLoading && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component='div'
            count={meta}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </Box>
  );
}

export default AdminDashboard;
